<template>
  <div class="mfv2-frame" :style="styles.frame" data-testid="mfcard-comparison-v2">
    <div v-if="text(cardModel.title)" :style="styles.title">{{ text(cardModel.title) }}</div>

    <div v-if="cardModel.tabs.length" class="mfv2-chips" :style="styles.tabRow">
      <button
        v-for="(tab, i) in cardModel.tabs"
        :key="'tab' + i"
        type="button"
        class="mfv2-chip"
        :style="tab === currentTab ? styles.tabSelected : styles.tab"
        :aria-pressed="tab === currentTab"
        :data-testid="'mfv2-tab-' + text(tab.key)"
        @click="selectTab(tab)"
      >{{ text(tab.label) }}</button>
    </div>

    <div v-if="currentTab && currentTab.periods && currentTab.periods.length" class="mfv2-chips" :style="styles.periodRow">
      <button
        v-for="(period, i) in currentTab.periods"
        :key="'period' + i"
        type="button"
        class="mfv2-chip"
        :style="period === currentPeriod ? styles.periodSelected : styles.period"
        :aria-pressed="period === currentPeriod"
        :data-testid="'mfv2-period-' + text(period.key)"
        @click="selectPeriod(period)"
      >{{ text(period.label) }}</button>
    </div>

    <div v-if="currentPeriod && text(currentPeriod.note)" :style="styles.note">{{ text(currentPeriod.note) }}</div>

    <!-- A table tab. Each column starts at the wire's fundColumnWidth and shares what is left over, so
         two funds fill the card and more of them scroll under the sticky label column. -->
    <div v-if="currentRows.length" class="mfv2-grid-wrap" :style="styles.gridWrap" data-testid="mfv2-grid">
      <table class="mfv2-grid" :style="gridView.tableStyle">
        <colgroup>
          <col :style="gridView.labelColumnStyle" />
          <col v-for="(fund, i) in gridView.funds" :key="'col' + i" />
        </colgroup>
        <thead>
          <tr>
            <td class="mfv2-sticky" :style="styles.headCorner"></td>
            <th v-for="(fund, i) in gridView.funds" :key="'fund' + i" scope="col" :style="styles.headCell">
              <div v-if="fund" class="mfv2-fund" :style="styles.fundHeader">
                <span class="mfv2-logo" :style="styles.logo" aria-hidden="true">
                  {{ text(fund.logoText) }}
                  <img
                    v-if="logoSrc(fund)"
                    :key="logoSrc(fund)"
                    :src="logoSrc(fund)"
                    class="mfv2-logo-img"
                    alt=""
                    @error="hideBrokenLogo"
                  />
                </span>
                <span class="mfv2-wrap" :style="styles.fundCode">{{ text(fund.code) }}</span>
                <span v-if="text(fund.name)" class="mfv2-wrap" :style="styles.fundName">{{ text(fund.name) }}</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, ri) in currentRows" :key="'row' + ri">
            <th scope="row" class="mfv2-sticky" :style="rowLabelStyle(row)">{{ text(row.label) }}</th>
            <td v-for="(cell, ci) in row.cells" :key="'cell' + ci" :style="cellStyle(row, cell)">
              <div class="mfv2-wrap">{{ text(cell.value) || '-' }}</div>
              <span v-if="cell.best && text(cardModel.bestLabel)" class="mfv2-best" :style="styles.best">
                {{ text(cardModel.bestLabel) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- The chart tab. Geometry lives in `chartView`; every size and colour here is a wire key. -->
    <template v-if="chartView">
      <div v-if="chartView.title" :style="styles.chartTitle">{{ chartView.title }}</div>

      <svg
        class="mfv2-chart"
        :style="styles.chartFrame"
        :viewBox="'0 0 ' + chartView.width + ' ' + chartView.height"
        preserveAspectRatio="xMinYMin meet"
        role="group"
        :aria-label="chartView.ariaLabel"
        data-testid="mfv2-chart"
      >
        <g aria-hidden="true">
          <line
            v-for="grid in chartView.gridLines"
            :key="grid.key"
            :x1="grid.x1"
            :x2="grid.x2"
            :y1="grid.y"
            :y2="grid.y"
            :stroke="chartView.gridColor"
            :stroke-width="chartView.gridWidth"
            :stroke-dasharray="chartView.gridDash"
          />
          <text
            v-for="label in chartView.yLabels"
            :key="label.key"
            :x="label.x"
            :y="label.y"
            :fill="label.color"
            :font-size="chartView.axisFontSize"
            dominant-baseline="central"
          >{{ label.text }}</text>
          <text
            v-for="label in chartView.xLabels"
            :key="label.key"
            :x="label.x"
            :y="label.y"
            :fill="chartView.axisColor"
            :font-size="chartView.axisFontSize"
            text-anchor="middle"
          >{{ label.text }}</text>
          <line
            v-if="chartView.guide"
            :x1="chartView.guide.x"
            :x2="chartView.guide.x"
            :y1="chartView.guide.y1"
            :y2="chartView.guide.y2"
            :stroke="chartView.guideColor"
            :stroke-width="chartView.guideWidth"
          />
          <polyline
            v-for="line in chartView.lines"
            :key="line.key"
            :points="line.points"
            :stroke="line.color"
            :stroke-width="chartView.lineWidth"
            fill="none"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
          <circle
            v-for="dot in chartView.dots"
            :key="dot.key"
            :cx="dot.x"
            :cy="dot.y"
            :r="chartView.dotRadius"
            :fill="dot.color"
          />
          <!-- The ring is the panel's colour, so a large dot stays readable where two lines cross. -->
          <circle
            v-for="dot in chartView.selectedDots"
            :key="dot.key"
            :cx="dot.x"
            :cy="dot.y"
            :r="chartView.selectedDotRadius"
            :fill="dot.color"
            :stroke="chartView.surfaceColor"
            stroke-width="2"
          />
        </g>
        <!-- One column per year, last so it sits on top: the reader aims at a year, not at a 6px dot. -->
        <rect
          v-for="hit in chartView.hits"
          :key="hit.key"
          class="mfv2-hit"
          :x="hit.x"
          y="0"
          :width="hit.width"
          :height="chartView.height"
          fill="transparent"
          role="button"
          tabindex="0"
          :aria-label="hit.ariaLabel"
          :aria-pressed="hit.index === chartView.selectedIndex"
          @click="selectYear(hit.index)"
          @keydown.enter.prevent="selectYear(hit.index)"
          @keydown.space.prevent="selectYear(hit.index)"
        />
      </svg>

      <div class="mfv2-legend" :style="styles.legend" data-testid="mfv2-legend">
        <div :style="styles.legendTitle">{{ chartView.legendHeading }}</div>
        <div v-for="row in chartView.legendRows" :key="row.key" class="mfv2-legend-row" :style="styles.legendRow">
          <span class="mfv2-legend-dot" :style="row.dotStyle" aria-hidden="true"></span>
          <span class="mfv2-logo" :style="styles.logo" aria-hidden="true">
            {{ text(row.fund.logoText) }}
            <img
              v-if="logoSrc(row.fund)"
              :key="logoSrc(row.fund)"
              :src="logoSrc(row.fund)"
              class="mfv2-logo-img"
              alt=""
              @error="hideBrokenLogo"
            />
          </span>
          <span class="mfv2-legend-fund" :style="styles.legendFund">{{ text(row.fund.code) }}</span>
          <span class="mfv2-legend-value" :style="row.valueStyle">{{ row.value }}</span>
        </div>
      </div>
    </template>

    <div v-if="text(cardModel.disclaimer)" class="mfv2-disclaimer" :style="styles.disclaimer">
      <v-icon
        icon="mdi-alert-circle-outline"
        class="mfv2-disclaimer-icon"
        :size="cardData.disclaimerIconFontSize || undefined"
        :style="styles.disclaimerIcon"
      />
      <span class="mfv2-wrap">{{ text(cardModel.disclaimer) }}</span>
    </div>

    <!-- Dead on purpose: the phone owns what this button does. -->
    <button
      v-if="text(cardModel.buttonLabel)"
      type="button"
      class="mfv2-buy"
      :style="styles.button"
      disabled
      data-testid="mfv2-button"
    >{{ text(cardModel.buttonLabel) }}</button>
  </div>
</template>

<script>
/**
 * MutualFundComparisonV2Preview — the builder's drawing of the V2 fund comparison card
 * (`TAX_FUND_COMPARISON_V2`, refId `type15`): four tabs, period chips, a table with a sticky label
 * column and a yearly return chart.
 *
 * `utils/mutualFundComparisonV2` holds the rules and their tests. This file only places what those
 * functions answer, so it stays free of decisions a node test could not reach.
 *
 * It draws two inputs the same way, which is why every text seat goes through `text`:
 *  - a rendered wire payload, once a fund is picked (plain strings);
 *  - the raw template, before one is picked: the "no data" card, with `{th,en,zh}` bags still in it.
 *
 * Three rules from the util apply here:
 *  - Styles are merged with `layerStyles`, never `Object.assign` or a `:style` array: `styleOf`
 *    answers null for an unauthored hint, and that null would erase the layer underneath.
 *  - A colour is converted once. `styleOf` / `boxOf` / `chartModel` already return CSS, and only a
 *    raw wire key goes through `hintColor`, which would swap the channels of `#AARRGGBB` a second time.
 *  - `chartModel` answers plot coordinates, so `chartView` offsets them for padding and axis labels.
 *
 * Selection is local state and falls back to what the card authored (`currentTab`, `currentPeriod`).
 * It is cleared by `selectionSignature` and not by every card change: the admin edits this card one
 * keystroke at a time, so a re-seed on any change would throw them off the chart tab while they
 * adjust a chart colour.
 */
import { hintColor } from '../utils/taxCardFormat';
import {
  boxOf,
  chartModel,
  formatChartValue,
  gradientOf,
  layerStyles,
  legendTitle,
  normalizeCard,
  styleOf,
} from '../utils/mutualFundComparisonV2';

const CARD_LANGS = ['th', 'en', 'zh'];

const SAFE_LOGO_URL = /^https?:\/\//i;

/** Used before the first measure: the dialog's 400px column less its padding and the card's. */
const DEFAULT_CHART_WIDTH = 344;

/** Only for a half-typed card missing the key. The wire always sets these. */
const FALLBACK = {
  labelColumnWidth: 100,
  fundColumnWidth: 84,
  chartHeight: 210,
  axisFontSize: 11,
  axisColor: '#5A5E7C',
  gridColor: '#D5D9E5',
  gridWidth: 1,
  gridDash: 4,
  guideColor: '#B9BED0',
  guideWidth: 1,
  lineWidth: 2.5,
  pointWidth: 6,
  surfaceColor: '#F7F8FB',
  dotWidth: 10,
};

/** Rough width of a digit in em. SVG text cannot be measured in a computed, and a guess suffices here. */
const DIGIT_EM = 0.6;

/** Between an axis label and the plot. Not a wire key. */
const AXIS_GAP = 6;

/** How much larger the dot on the selected year is. The wire sets only `chartPointWidth`. */
const SELECTED_DOT_GROWTH = 2;

/** What the legend heading shows for its `{label}` while the template has no years. */
const UNFILLED_LABEL = '…';

/** A text seat as a string: plain, or a language bag's first filled language. */
function seatText(v) {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (!v || typeof v !== 'object' || Array.isArray(v)) return '';
  const lang = CARD_LANGS.find((key) => typeof v[key] === 'string' && v[key].trim() !== '');
  return lang ? v[lang] : '';
}

function px(v) {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? `${v}px` : null;
}

function positive(v, fallback) {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
}

function notNegative(v) {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0;
}

/** A divider as one `border-bottom`, or nothing when either half is unusable. */
function bottomLine(color, width) {
  const css = hintColor(color);
  const size = px(width);
  return css && size ? { borderBottom: `${size} solid ${css}` } : null;
}

/** The value's colour, following what is printed: `0.00%` and `-` claim no direction. */
function signColor(value, printed, up, down) {
  if (!/[1-9]/.test(printed)) return null;
  return value > 0 ? up : down;
}

export default {
  name: 'MutualFundComparisonV2Preview',
  props: {
    /** The envelope `{ type, version, refId, data }`, wire or template. */
    card: { type: Object, default: null },
  },
  data() {
    return {
      /** The tab the admin picked. Empty ⇒ `selectedTab`. */
      activeTab: '',
      /** Tab key → the period the admin picked there. Absent ⇒ that tab's `selectedPeriod`. */
      activePeriods: {},
      /** Index into the points the chart currently keeps. Null ⇒ the latest year. */
      chartIndex: null,
      /** The card's content width in px, so the SVG draws 1:1 and its fonts keep their authored size. */
      chartWidth: DEFAULT_CHART_WIDTH,
      resizeObserver: null,
    };
  },
  computed: {
    cardData() {
      const data = this.card && this.card.data;
      return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    },

    cardModel() {
      return normalizeCard(this.cardData);
    },

    /** Only what the selection depends on: the authored defaults and the keys they choose between. */
    selectionSignature() {
      const { selectedTab, tabs } = this.cardModel;
      return JSON.stringify([
        selectedTab,
        tabs.map((tab) => [tab.key, tab.selectedPeriod, (tab.periods || []).map((period) => period.key)]),
      ]);
    },

    currentTab() {
      const { tabs, selectedTab } = this.cardModel;
      return tabs.find((tab) => tab.key === this.activeTab)
        || tabs.find((tab) => tab.key === selectedTab)
        || tabs[0]
        || null;
    },

    currentPeriod() {
      const tab = this.currentTab;
      const periods = tab && tab.periods;
      if (!periods || !periods.length) return null;
      return periods.find((period) => period.key === this.activePeriods[tab.key])
        || periods.find((period) => period.key === tab.selectedPeriod)
        || periods[0];
    },

    currentRows() {
      const tab = this.currentTab;
      if (!tab || tab.chart) return [];
      if (tab.rows) return tab.rows;
      return this.currentPeriod ? this.currentPeriod.rows : [];
    },

    /** Columns come from the widest thing on the tab, so a row with an extra cell is not cut off. */
    gridView() {
      const data = this.cardData;
      const { funds } = this.cardModel;
      const count = Math.max(funds.length, ...this.currentRows.map((row) => row.cells.length));
      const labelWidth = positive(data.labelColumnWidth, FALLBACK.labelColumnWidth);
      const fundWidth = positive(data.fundColumnWidth, FALLBACK.fundColumnWidth);
      return {
        funds: Array.from({ length: count }, (_, i) => funds[i] || null),
        tableStyle: { width: `max(100%, ${labelWidth + count * fundWidth}px)` },
        labelColumnStyle: { width: `${labelWidth}px` },
      };
    },

    /** Every style that depends only on the card, in one place. */
    styles() {
      const data = this.cardData;
      const gridFill = hintColor(data.gridFrameColor);
      const cellBox = boxOf(data, 'cell');
      const rowDivider = bottomLine(data.rowDividerColor, data.rowDividerHeight);
      const stickyCell = {
        backgroundColor: gridFill,
        '--mfv2-sticky-width': px(data.stickyDividerWidth),
        '--mfv2-sticky-color': hintColor(data.stickyDividerColor),
      };
      const gradient = gradientOf(data);
      const tabHeight = { height: px(data.tabHeight) };
      const periodHeight = { height: px(data.periodHeight) };
      const logoWidth = px(data.logoWidth);
      const legendSpacing = px(data.legendSpacing);
      return {
        frame: layerStyles(
          boxOf(data, 'main'),
          { marginTop: px(data.mainMarginTop), marginBottom: px(data.mainMarginBottom), gap: px(data.mainSpacing) },
          gradient && { backgroundImage: gradient.background, backgroundSize: `100% ${gradient.height}` },
        ),
        title: styleOf(data, 'title'),
        tabRow: { gap: px(data.tabSpacing) },
        tab: layerStyles(boxOf(data, 'tab'), styleOf(data, 'tab'), tabHeight),
        tabSelected: layerStyles(
          boxOf(data, 'tab'),
          boxOf(data, 'tabSelected'),
          styleOf(data, 'tab'),
          styleOf(data, 'tabSelected'),
          tabHeight,
        ),
        periodRow: { gap: px(data.periodSpacing) },
        period: layerStyles(boxOf(data, 'period'), styleOf(data, 'period'), periodHeight),
        periodSelected: layerStyles(
          boxOf(data, 'period'),
          boxOf(data, 'periodSelected'),
          styleOf(data, 'period'),
          styleOf(data, 'periodSelected'),
          periodHeight,
        ),
        note: styleOf(data, 'note'),
        gridWrap: { backgroundColor: gridFill },
        cellBox,
        rowDivider,
        stickyCell,
        headCorner: layerStyles(cellBox, rowDivider, stickyCell),
        headCell: layerStyles(cellBox, rowDivider),
        fundHeader: { gap: px(data.fundHeaderSpacing) },
        logo: layerStyles(boxOf(data, 'logo'), styleOf(data, 'logo'), { width: logoWidth, height: logoWidth }),
        fundCode: styleOf(data, 'fundCode'),
        fundName: styleOf(data, 'fundName'),
        best: layerStyles(boxOf(data, 'best'), styleOf(data, 'best'), { marginTop: px(data.bestSpacing) }),
        chartTitle: styleOf(data, 'chartTitle'),
        // Not `boxOf`: its padding on an <svg> would shrink the drawing area under the viewBox.
        chartFrame: layerStyles({
          backgroundColor: hintColor(data.chartFrameColor),
          borderRadius: px(data.chartCornerRadius),
          height: `${positive(data.chartHeight, FALLBACK.chartHeight)}px`,
        }),
        legend: boxOf(data, 'legend'),
        legendTitle: styleOf(data, 'legendTitle'),
        legendRow: layerStyles(
          { marginTop: legendSpacing, paddingBottom: legendSpacing },
          bottomLine(data.legendDividerColor, data.legendDividerHeight),
        ),
        legendFund: styleOf(data, 'legendFund'),
        disclaimer: layerStyles(boxOf(data, 'disclaimer'), styleOf(data, 'disclaimer'), { gap: px(data.disclaimerSpacing) }),
        disclaimerIcon: { color: hintColor(data.disclaimerIconColor) },
        button: layerStyles(boxOf(data, 'button'), styleOf(data, 'button'), { height: px(data.buttonHeight) }),
      };
    },

    /**
     * The chart tab, in SVG coordinates: plot area inside the wire's padding, with room on the left
     * for y labels and at the bottom for x labels.
     */
    chartView() {
      const tab = this.currentTab;
      const chart = tab && tab.chart;
      if (!chart) return null;

      const data = this.cardData;
      const { funds } = this.cardModel;
      const width = this.chartWidth;
      const height = positive(data.chartHeight, FALLBACK.chartHeight);
      const axisFontSize = positive(data.chartAxisFontSize, FALLBACK.axisFontSize);
      const axisColor = hintColor(data.chartAxisColor) || FALLBACK.axisColor;
      const upColor = hintColor(data.positiveColor) || axisColor;
      const downColor = hintColor(data.negativeColor) || axisColor;
      const paddingLeft = notNegative(data.chartPaddingLeft);
      const paddingBottom = notNegative(data.chartPaddingBottom);

      const request = {
        points: chart.points,
        funds,
        count: this.currentPeriod ? this.currentPeriod.count : null,
        selectedIndex: this.chartIndex,
        axisDecimals: chart.axisDecimals,
      };

      // The y gutter depends on the tick labels, and the domain sets those, not the plot's size.
      const probe = chartModel(request);
      const tickChars = Math.max(...probe.ys.ticks.map((tick) => tick.label.length));
      const yearChars = Math.max(0, ...probe.points.map((point) => seatText(point.label).length));
      const yearWidth = yearChars * axisFontSize * DIGIT_EM;

      const plotLeft = paddingLeft + Math.ceil(tickChars * axisFontSize * DIGIT_EM) + AXIS_GAP;
      const plotRight = width - notNegative(data.chartPaddingRight);
      const plotTop = notNegative(data.chartPaddingTop) + axisFontSize / 2;
      const plotBottom = height - paddingBottom - axisFontSize - AXIS_GAP;
      // The first and last years sit half a label in from the gridlines' ends, so their labels fit.
      const inset = Math.ceil(yearWidth / 2);

      const model = chartModel({
        ...request,
        width: Math.max(1, plotRight - plotLeft - inset * 2),
        height: Math.max(1, plotBottom - plotTop),
      });
      const xOf = (x) => plotLeft + inset + x;
      const yOf = (y) => plotTop + y;
      const xs = model.xs.map(xOf);
      const seriesColor = (series) => series.color || axisColor;
      const point = model.selectedIndex === null ? null : model.points[model.selectedIndex];

      // Only as many year labels as fit, counted back from the latest so that year always has one.
      const step = xs.length > 1 ? xs[1] - xs[0] : Infinity;
      const labelEvery = Math.max(1, Math.ceil((yearWidth + 2) / step));

      const yearSummary = (entry) => [
        seatText(entry.label),
        ...funds.map((fund, i) => `${seatText(fund.code)} ${formatChartValue(entry.values[i], chart)}`),
      ].join(', ');

      const shownValues = point ? point.values : [];
      const yearRange = model.points.length
        ? `${seatText(model.points[0].label)}–${seatText(model.points[model.points.length - 1].label)}`
        : '';

      return {
        width,
        height,
        title: seatText(chart.title),
        ariaLabel: [seatText(chart.title), funds.map((fund) => seatText(fund.code)).join(', '), yearRange]
          .filter(Boolean)
          .join(' · '),
        axisFontSize,
        axisColor,
        gridColor: hintColor(data.chartGridColor) || FALLBACK.gridColor,
        gridWidth: positive(data.chartGridWidth, FALLBACK.gridWidth),
        gridDash: positive(data.chartGridDashLength, FALLBACK.gridDash),
        guideColor: hintColor(data.chartGuideColor) || FALLBACK.guideColor,
        guideWidth: positive(data.chartGuideWidth, FALLBACK.guideWidth),
        lineWidth: positive(data.chartLineWidth, FALLBACK.lineWidth),
        dotRadius: positive(data.chartPointWidth, FALLBACK.pointWidth) / 2,
        selectedDotRadius: positive(data.chartPointWidth, FALLBACK.pointWidth) / 2 + SELECTED_DOT_GROWTH,
        surfaceColor: hintColor(data.chartFrameColor) || FALLBACK.surfaceColor,
        selectedIndex: model.selectedIndex,

        gridLines: model.ys.ticks.map((tick, i) => ({ key: i, x1: plotLeft, x2: plotRight, y: yOf(tick.y) })),
        yLabels: model.ys.ticks.map((tick, i) => {
          let color = axisColor;
          if (tick.value > 0) color = upColor;
          else if (tick.value < 0) color = downColor;
          return { key: i, x: paddingLeft, y: yOf(tick.y), text: tick.label, color };
        }),
        xLabels: model.points
          .map((entry, i) => ({ key: i, x: xs[i], y: height - paddingBottom, text: seatText(entry.label) }))
          .filter((_, i) => (xs.length - 1 - i) % labelEvery === 0),
        guide: model.guideX === null ? null : { x: xOf(model.guideX), y1: plotTop, y2: plotBottom },
        lines: model.series.flatMap((series) => series.segments
          // A one-point segment has no line to draw. Its dot is below.
          .filter((segment) => segment.length > 1)
          .map((segment, i) => ({
            key: `${series.index}-${i}`,
            color: seriesColor(series),
            points: segment.map((at) => `${xOf(at.x)},${yOf(at.y)}`).join(' '),
          }))),
        dots: model.series.flatMap((series) => series.dots.map((dot, i) => ({
          key: `${series.index}-${i}`,
          x: xOf(dot.x),
          y: yOf(dot.y),
          color: seriesColor(series),
        }))),
        selectedDots: model.series.flatMap((series) => series.dots
          .filter((dot) => dot.x === model.guideX)
          .map((dot) => ({ key: series.index, x: xOf(dot.x), y: yOf(dot.y), color: seriesColor(series) }))),
        hits: xs.map((x, i) => {
          const left = i === 0 ? plotLeft : (xs[i - 1] + x) / 2;
          const right = i === xs.length - 1 ? plotRight : (x + xs[i + 1]) / 2;
          return { key: i, index: i, x: left, width: right - left, ariaLabel: yearSummary(model.points[i]) };
        }),

        legendHeading: legendTitle(seatText(chart.legendTitle), point ? seatText(point.label) : UNFILLED_LABEL),
        legendRows: funds.map((fund, i) => {
          const value = shownValues[i];
          const printed = formatChartValue(value, chart);
          const dotWidth = px(positive(data.legendDotWidth, FALLBACK.dotWidth));
          return {
            key: i,
            fund,
            value: printed,
            dotStyle: { width: dotWidth, height: dotWidth, backgroundColor: seriesColor(model.series[i]) },
            valueStyle: layerStyles(styleOf(data, 'legendValue'), { color: signColor(value, printed, upColor, downColor) }),
          };
        }),
      };
    },
  },
  watch: {
    selectionSignature() {
      this.activeTab = '';
      this.activePeriods = {};
      this.chartIndex = null;
    },
  },
  mounted() {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0] ? entries[0].contentRect.width : 0;
      if (width > 0) this.chartWidth = Math.round(width);
    });
    this.resizeObserver.observe(this.$el);
  },
  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    text: seatText,

    logoSrc(fund) {
      const url = seatText(fund.logoUrl).trim();
      return SAFE_LOGO_URL.test(url) ? url : '';
    },

    /** The `logoText` behind the image shows instead of a broken-image glyph. */
    hideBrokenLogo(event) {
      event.target.style.display = 'none';
    },

    rowFill(row) {
      const highlighted = row.key && row.key === this.cardModel.highlightRow;
      return { backgroundColor: highlighted ? hintColor(this.cardData.rowHighlightColor) : null };
    },

    rowLabelStyle(row) {
      const { cellBox, rowDivider, stickyCell } = this.styles;
      return layerStyles(
        cellBox,
        rowDivider,
        stickyCell,
        styleOf(this.cardData, 'rowLabel'),
        styleOf(row, 'label'),
        this.rowFill(row),
      );
    },

    cellStyle(row, cell) {
      const { cellBox, rowDivider } = this.styles;
      return layerStyles(
        cellBox,
        rowDivider,
        styleOf(this.cardData, 'cell'),
        styleOf(cell, 'value'),
        this.rowFill(row),
      );
    },

    selectTab(tab) {
      this.activeTab = tab.key;
    },

    selectPeriod(period) {
      this.activePeriods = { ...this.activePeriods, [this.currentTab.key]: period.key };
      // The index belongs to the kept points, so on a different chip it would mean a different year.
      this.chartIndex = null;
    },

    selectYear(index) {
      this.chartIndex = index;
    },
  },
};
</script>

<style scoped>
/* Every colour and size below is a fallback for a half-typed card: the wire's keys arrive inline. */

/* `width: 0; min-width: 100%` keeps the non-wrapping tab strip out of this box's intrinsic width.
   The dialog's preview column is a flex item, and without this the strip would stretch the column
   instead of scrolling inside it. */
.mfv2-frame {
  box-sizing: border-box;
  width: 0;
  min-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background-color: #ffffff;
  background-repeat: no-repeat;
  border: 1px solid #e1e4ee;
  border-radius: 16px;
  color: #1c1e33;
  line-height: 1.35;
}

.mfv2-chips {
  display: flex;
  gap: 6px;
  flex: 0 0 auto;
  overflow-x: auto;
  /* Left visible: a mouse without a horizontal wheel has no other way to reach the last tab. */
  scrollbar-width: thin;
}

.mfv2-chip,
.mfv2-buy {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
}

.mfv2-chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  cursor: pointer;
}

.mfv2-chip:focus-visible {
  outline: 2px solid #5a5e7c;
  outline-offset: -2px;
}

/* ── table ───────────────────────────────────────────────────────────────────────── */
.mfv2-grid-wrap {
  overflow-x: auto;
}

/* `separate`: with collapsed borders a cell's border stays behind when the cell scrolls or sticks. */
.mfv2-grid {
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
}

.mfv2-grid th,
.mfv2-grid td {
  vertical-align: middle;
  font-weight: inherit;
  font-variant-numeric: tabular-nums;
}

.mfv2-grid tbody th {
  text-align: left;
}

.mfv2-sticky {
  position: sticky;
  left: 0;
  z-index: 1;
  background-color: #ffffff;
}

/* Hangs over the next column instead of taking layout, because it is a shadow of the sticky column. */
.mfv2-sticky::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 100%;
  width: var(--mfv2-sticky-width, 0);
  background: linear-gradient(to right, var(--mfv2-sticky-color, transparent), transparent);
  pointer-events: none;
}

.mfv2-fund {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}

/* `font-weight: inherit`: assets/base.css resets it on `*`, so a weight authored on the td or the bar
   would stop at its parent and never reach the text nested inside. */
.mfv2-wrap {
  overflow-wrap: anywhere;
  font-weight: inherit;
}

.mfv2-logo {
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 50%;
  line-height: 1;
}

.mfv2-logo-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mfv2-best {
  display: block;
  width: max-content;
  margin-left: auto;
  margin-right: auto;
  line-height: 1;
}

/* ── chart ───────────────────────────────────────────────────────────────────────── */
.mfv2-chart {
  display: block;
  flex: 0 0 auto;
  width: 100%;
}

.mfv2-hit {
  cursor: pointer;
}

/* A stroke, because `outline` on an SVG shape is not drawn by every browser. */
.mfv2-hit:focus-visible {
  outline: none;
  stroke: #5a5e7c;
  stroke-width: 1.5px;
}

.mfv2-legend-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mfv2-legend-dot {
  flex: 0 0 auto;
  border-radius: 50%;
}

.mfv2-legend-fund {
  flex: 1 1 0;
  min-width: 0;
  overflow-wrap: anywhere;
}

.mfv2-legend-value {
  flex: 0 0 auto;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* ── foot ────────────────────────────────────────────────────────────────────────── */
.mfv2-disclaimer {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mfv2-disclaimer-icon {
  flex: 0 0 auto;
}

.mfv2-buy {
  flex: 0 0 auto;
  width: 100%;
  cursor: default;
}
</style>
